// waiting SW 適用が pwaUpdateDefer（$pwa / 初期化完了依存）一本だと、
// 初期化ハング時に SKIP_WAITING が永遠に飛ばず更新が停滞する単一障害点になる。
// 本プラグインは enforce:"pre" の極小安全網で、アプリ本体初期化に依存せず
// 「waiting 検出 → N 秒後に無条件 SKIP_WAITING」を行う。

/**
 * pwaUpdateDefer の MAX_WAIT_MS(180s) + POST_IDLE_SETTLE_MS(3s) より長く、通常パスが先に動く想定。
 * 不変条件: この値 > MAX_WAIT_MS + POST_IDLE_SETTLE_MS。逆転すると本安全網が先に発火し、
 * 正当に継続中のブートストラップへ reload が割り込む (prompt 方式化の目的を損なう)。
 */
const FALLBACK_SKIP_WAITING_MS = 200_000;
/** SW registration がまだ無いときに再試行する間隔 */
const REGISTRATION_RETRY_MS = 1_000;

export default defineNuxtPlugin({
  name: "pwa-update-timeout-fallback",
  enforce: "pre",
  setup() {
    // defer 側と同様、dev では更新適用を行わない
    if (import.meta.dev) {
      return;
    }
    if (!("serviceWorker" in navigator)) {
      return;
    }

    // 新 SW からの移行判定 ping に応答する (#33449)。
    // 新 SW は install 時に「SKIP_WAITING を送れるクライアントが居るか」を ping で確かめ、
    // 無応答なら自己 skipWaiting して移行する (app/service-worker/sw.ts)。
    // 応答実装は新バンドルにしか無いため、旧 autoUpdate バンドル配下では構造的に無応答となる。
    // enforce:"pre" の本プラグインに置くことで、重い初期化の完了を待たずに応答できる。
    navigator.serviceWorker.addEventListener("message", (event: MessageEvent) => {
      if (event.data?.type === "PING_PROMPT_CAPABLE") {
        event.ports[0]?.postMessage({ type: "PROMPT_CAPABLE" });
      }
    });

    let timerId: ReturnType<typeof setTimeout> | null = null;
    let armedFor: ServiceWorker | null = null;
    let fired = false;

    const arm = (waiting: ServiceWorker) => {
      if (fired || armedFor === waiting) {
        return;
      }
      if (timerId != null) {
        clearTimeout(timerId);
      }
      armedFor = waiting;
      timerId = setTimeout(() => {
        timerId = null;
        if (fired) {
          return;
        }
        // 発火時点でも waiting が残っていれば無条件 SKIP_WAITING
        void navigator.serviceWorker.getRegistration().then((reg) => {
          const stillWaiting = reg?.waiting;
          if (!stillWaiting) {
            armedFor = null;
            return;
          }
          fired = true;
          const isUpdate = Boolean(navigator.serviceWorker.controller);
          stillWaiting.postMessage({ type: "SKIP_WAITING" });
          // 更新時は新 SW 制御後に資産を取り直す（workbox 未起動でも回復できるよう自前 reload）
          if (isUpdate) {
            let reloaded = false;
            let backupId: ReturnType<typeof setTimeout> | null = null;
            const reload = () => {
              if (reloaded) {
                return;
              }
              reloaded = true;
              if (backupId != null) {
                clearTimeout(backupId);
                backupId = null;
              }
              window.location.reload();
            };
            navigator.serviceWorker.addEventListener("controllerchange", reload, { once: true });
            // controllerchange が来ない場合の保険（controllerchange 発火後は上で clear される）
            backupId = setTimeout(reload, 3_000);
          }
        });
      }, FALLBACK_SKIP_WAITING_MS);
    };

    const watch = (reg: ServiceWorkerRegistration) => {
      if (reg.waiting) {
        arm(reg.waiting);
      }
      reg.addEventListener("updatefound", () => {
        const installing = reg.installing;
        if (!installing) {
          return;
        }
        installing.addEventListener("statechange", () => {
          if (installing.state === "installed" && reg.waiting) {
            arm(reg.waiting);
          }
        });
      });
    };

    // registration は本プラグインより後に完了し得る（vite-pwa）。アプリ init 非依存で待つ。
    const tryAttach = () => {
      void navigator.serviceWorker.getRegistration().then((reg) => {
        if (reg) {
          watch(reg);
          return;
        }
        setTimeout(tryAttach, REGISTRATION_RETRY_MS);
      });
    };
    tryAttach();
  },
});
